import React, { useState, useEffect } from "react";
import "./SupportTicket.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import useMediaQuery from "@mui/material/useMediaQuery";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import moment from "moment";
import TicketModal from "./TicketModal";
import TicketDetailModal from "./TicketDetailModal";

function SupportTicket() {
  const uid = localStorage.getItem("id");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ticketTypeFilter, setReportTypeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [totalReports, setTotalReports] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Click-outside counters
  const [statusClicks, setStatusClicks] = useState(0);
  const [typeClicks, setTypeClicks] = useState(0);
  const [priorityClicks, setPriorityClicks] = useState(0);

  // Currently open menu
  const [openMenu, setOpenMenu] = useState(null);

  const isMobile = useMediaQuery("(max-width:880px)");
  const isSmallMobile = useMediaQuery("(max-width:480px)");
  
  // View mode: 'grid' or 'list' - default to list
  const [viewMode, setViewMode] = useState('list');

  const handleMenuOpen = (menu) => {
    setOpenMenu(menu);
    if (menu !== "status") setStatusClicks(0);
    if (menu !== "type") setTypeClicks(0);
    if (menu !== "priority") setPriorityClicks(0);
  };

  useEffect(() => {
    getReports();
  }, [page, searchTerm, statusFilter, ticketTypeFilter, priorityFilter]);

  const getReports = () => {
    setLoading(true);
    AxiosUrl.post(`/fetch-reports/${uid}`, {
      page,
      limit,
      search: searchTerm,
      status: statusFilter,
      ticketType: ticketTypeFilter,
      priority: priorityFilter,
    })
      .then((res) => {
        if (res.data.Error) {
          toast.error(res.data.Error);
          setLoading(false);
          return;
        }
        setReports(res.data.tickets || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalReports(res.data.totalTickets || 0);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to fetch support tickets");
        setLoading(false);
      });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleReportTypeChange = (event) => {
    setReportTypeFilter(event.target.value);
    setPage(1);
  };

  const handlePriorityChange = (event) => {
    setPriorityFilter(event.target.value);
    setPage(1);
  };

  const getStatusDisplayText = (value) => {
    switch (value) {
      case "pending":
        return "Pending";
      case "in-progress":
        return "In Progress";
      case "resolved":
        return "Resolved";
      case "closed":
        return "Closed";
      default:
        return "All Status";
    }
  };

  const getTypeDisplayText = (value) => {
    switch (value) {
      case "user":
        return "User Issue";
      case "payment":
        return "Payment Issue";
      case "subscription":
        return "Subscription Issue";
      case "general":
        return "General Query";
      case "bug":
        return "Bug Report";
      case "feature":
        return "Feature Request";
      default:
        return "All Types";
    }
  };

  const getPriorityDisplayText = (value) => {
    switch (value) {
      case "low":
        return "Low";
      case "medium":
        return "Medium";
      case "high":
        return "High";
      case "critical":
        return "Critical";
      default:
        return "All Priority";
    }
  };

  const handleCreateReport = () => {
    setEditingReport(null);
    setShowModal(true);
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setShowModal(true);
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
  };

  const handleDetailModalClose = () => {
    setShowDetailModal(false);
    setSelectedTicket(null);
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm("Are you sure you want to delete this report?")) {
      try {
        const response = await AxiosUrl.post(`/delete-report/${uid}`, {
          reportId,
        });

        if (response.data.success) {
          toast.success(response.data.message);
          getReports();
        } else {
          toast.error(response.data.error || "Failed to delete report");
        }
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Failed to delete report");
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingReport(null);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingReport(null);
    getReports();
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "pending":
        return "status-pending";
      case "in-progress":
        return "status-in-progress";
      case "resolved":
        return "status-resolved";
      case "closed":
        return "status-closed";
      default:
        return "";
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "low":
        return "priority-low";
      case "medium":
        return "priority-medium";
      case "high":
        return "priority-high";
      case "critical":
        return "priority-critical";
      default:
        return "";
    }
  };

  const getTypeClass = (type) => {
    switch (type) {
      case "user":
        return "type-user";
      case "payment":
        return "type-payment";
      case "subscription":
        return "type-subscription";
      case "general":
        return "type-general";
      case "bug":
        return "type-bug";
      case "feature":
        return "type-feature";
      default:
        return "";
    }
  };

  return (
    <div className="tickets-container">
      <ToastContainer />
      <div className="tickets-main">
        <div className="tickets-content-wrapper">
          <div className="tickets-create-section">
            <Button
              className="create-ticket-button"
              onClick={handleCreateReport}
              variant="contained"
              startIcon={<AddIcon />}
            >
              Create New Ticket
            </Button>
          </div>

          <div className="tickets-filter-section">
            <div>
              <label className="tickets-filter-label" style={{ backgroundColor: "#e91e63" }}>
                Search
              </label>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search by title or description..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="tickets-search-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <ClickAwayListener
              onClickAway={() => {
                if (openMenu === "status") {
                  if (statusClicks > 0) {
                    setOpenMenu(null);
                  } else {
                    setStatusClicks(statusClicks + 1);
                  }
                }
              }}
            >
              <div>
                <label className="tickets-filter-label" style={{ backgroundColor: "#388e3c" }}>
                  Status
                </label>
                <TextField
                  select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  variant="outlined"
                  size="small"
                  className="tickets-status-filter"
                  onClick={() => handleMenuOpen("status")}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    },
                    displayEmpty: true,
                    renderValue: () => getStatusDisplayText(statusFilter),
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>

            <ClickAwayListener
              onClickAway={() => {
                if (openMenu === "type") {
                  if (typeClicks > 0) {
                    setOpenMenu(null);
                  } else {
                    setTypeClicks(typeClicks + 1);
                  }
                }
              }}
            >
              <div>
                <label className="tickets-filter-label" style={{ backgroundColor: "#3f51b5" }}>
                  Type
                </label>
                <TextField
                  select
                  value={ticketTypeFilter}
                  onChange={handleReportTypeChange}
                  variant="outlined"
                  size="small"
                  className="tickets-type-filter"
                  onClick={() => handleMenuOpen("type")}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    },
                    displayEmpty: true,
                    renderValue: () => getTypeDisplayText(ticketTypeFilter),
                  }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="user">User Issue</MenuItem>
                  <MenuItem value="payment">Payment Issue</MenuItem>
                  <MenuItem value="subscription">Subscription Issue</MenuItem>
                  <MenuItem value="general">General Query</MenuItem>
                  <MenuItem value="bug">Bug Report</MenuItem>
                  <MenuItem value="feature">Feature Request</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>

            <ClickAwayListener
              onClickAway={() => {
                if (openMenu === "priority") {
                  if (priorityClicks > 0) {
                    setOpenMenu(null);
                  } else {
                    setPriorityClicks(priorityClicks + 1);
                  }
                }
              }}
            >
              <div>
                <label className="tickets-filter-label" style={{ backgroundColor: "#f57c00" }}>
                  Priority
                </label>
                <TextField
                  select
                  value={priorityFilter}
                  onChange={handlePriorityChange}
                  variant="outlined"
                  size="small"
                  className="tickets-priority-filter"
                  onClick={() => handleMenuOpen("priority")}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    },
                    displayEmpty: true,
                    renderValue: () => getPriorityDisplayText(priorityFilter),
                  }}
                >
                  <MenuItem value="">All Priority</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>
          </div>

          {/* View Toggle Buttons */}
          <div className="tickets-view-toggle">
            <IconButton
              onClick={() => setViewMode('grid')}
              color={viewMode === 'grid' ? 'primary' : 'default'}
              title="Grid View"
              style={{
                backgroundColor: viewMode === 'grid' ? '#1976d2' : '#f5f5f5',
                color: viewMode === 'grid' ? 'white' : '#666',
                marginRight: '8px'
              }}
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
              title="List View"
              style={{
                backgroundColor: viewMode === 'list' ? '#1976d2' : '#f5f5f5',
                color: viewMode === 'list' ? 'white' : '#666'
              }}
            >
              <ViewListIcon />
            </IconButton>
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="tickets-grid-container">
              {loading && <div className="google-progress-bar"></div>}
              {reports.length <= 0 && !loading ? (
                <div className="tickets-no-data-available">
                  No reports available
                </div>
              ) : (
                <div className="tickets-grid">
                  {reports.map((report) => (
                    <Card key={report._id} className="tickets-grid-card">
                      <CardContent className="tickets-grid-card-content">
                        <div className="tickets-grid-header">
                          <div className="tickets-grid-icon-wrapper">
                            <SupportAgentIcon className="tickets-grid-icon" />
                          </div>
                          <div className="tickets-grid-header-info">
                            <Typography variant="h6" className="tickets-grid-title">
                              {report.title}
                            </Typography>
                            <div className="tickets-grid-badges">
                              <Chip
                                label={report.ticketType}
                                size="small"
                                className={`tickets-grid-type-chip ${getTypeClass(report.ticketType)}`}
                                style={{
                                  backgroundColor: 
                                    report.ticketType === "user" ? "#2196f3" :
                                    report.ticketType === "payment" ? "#4caf50" :
                                    report.ticketType === "subscription" ? "#ff9800" :
                                    report.ticketType === "general" ? "#9e9e9e" :
                                    report.ticketType === "bug" ? "#f44336" :
                                    report.ticketType === "feature" ? "#9c27b0" : "#607d8b",
                                  color: "white",
                                  fontSize: "10px",
                                  height: "22px",
                                  fontWeight: "600",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                                }}
                              />
                              <Chip
                                label={report.priority}
                                size="small"
                                className={`tickets-grid-priority-chip ${getPriorityClass(report.priority)}`}
                                style={{
                                  backgroundColor: 
                                    report.priority === "low" ? "#4caf50" :
                                    report.priority === "medium" ? "#ff9800" :
                                    report.priority === "high" ? "#ff5722" :
                                    report.priority === "critical" ? "#d32f2f" : "#9e9e9e",
                                  color: "white",
                                  fontSize: "10px",
                                  height: "22px",
                                  fontWeight: "600",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="tickets-grid-description">
                          <Typography variant="body2" className="tickets-grid-description-text">
                            {report.description.length > 150
                              ? report.description.substring(0, 150) + "..."
                              : report.description}
                          </Typography>
                        </div>
                        
                        <div className="tickets-grid-details">
                          <div className="tickets-grid-detail-item">
                            <Typography variant="caption" className="tickets-grid-label">Status:</Typography>
                            <Chip
                              label={report.status === "in-progress" ? "In Progress" : report.status}
                              size="small"
                              className={`tickets-grid-status-chip ${getStatusClass(report.status)}`}
                              style={{
                                backgroundColor: 
                                  report.status === "pending" ? "#ff9800" :
                                  report.status === "in-progress" ? "#2196f3" :
                                  report.status === "resolved" ? "#4caf50" :
                                  report.status === "closed" ? "#9e9e9e" : "#607d8b",
                                color: "white",
                                fontSize: "11px",
                                height: "24px",
                                fontWeight: "600",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                              }}
                            />
                          </div>
                          
                          {!isMobile && (
                            <div className="tickets-grid-detail-item">
                              <Typography variant="caption" className="tickets-grid-label">Created:</Typography>
                              <Typography variant="body2" className="tickets-grid-value">
                                {moment(report.createdAt).utcOffset('+06:00').format("DD MMM YYYY")}
                              </Typography>
                            </div>
                          )}
                        </div>
                      </CardContent>
                      <CardActions className="tickets-grid-actions">
                        <Button
                          className="report-view-button"
                          onClick={() => handleViewTicket(report)}
                          size="small"
                          startIcon={<VisibilityIcon />}
                        >
                          View
                        </Button>
                        <Button
                          className="report-edit-button"
                          onClick={() => handleEditReport(report)}
                          size="small"
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                        <Button
                          className="report-delete-button"
                          onClick={() => handleDeleteReport(report._id)}
                          size="small"
                          startIcon={<DeleteIcon />}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* List/Table View */}
          {viewMode === 'list' && (
          <TableContainer component={Paper} className="tickets-table-container">
            {loading && <div className="google-progress-bar"></div>}
            <Table className="tickets-table" aria-label="reports table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Title</TableCell>
                  {!isSmallMobile && <TableCell align="left">Description</TableCell>}
                  <TableCell align="center">Type</TableCell>
                  <TableCell align="center">Priority</TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Created</TableCell>
                    </>
                  )}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length <= 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? (isSmallMobile ? 4 : 5) : 7} align="center">
                      <div className="tickets-no-data-available">No reports available</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map((report) => (
                    <TableRow key={report._id} className="tickets-table-row">
                      <TableCell align="left">
                        <div className="report-title">{report.title}</div>
                      </TableCell>
                      {!isSmallMobile && (
                        <TableCell align="left">
                          <div className="ticket-description">
                            {report.description.length > 100
                              ? report.description.substring(0, 100) + "..."
                              : report.description}
                          </div>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Button className={`ticket-type-button ${getTypeClass(report.ticketType)}`}>
                          {report.ticketType}
                        </Button>
                      </TableCell>
                      <TableCell align="center">
                        <Button className={`ticket-priority-button ${getPriorityClass(report.priority)}`}>
                          {report.priority}
                        </Button>
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell align="center">
                            <Button className={`ticket-status-button ${getStatusClass(report.status)}`}>
                              {report.status === "in-progress" ? "In Progress" : report.status}
                            </Button>
                          </TableCell>
                          <TableCell align="center">
                            <span className="report-date">
                              {moment(report.createdAt).utcOffset('+06:00').format("DD MMM YYYY")}
                            </span>
                          </TableCell>
                        </>
                      )}
                      <TableCell align="center">
                        <div className="report-actions">
                          <Button
                            className="report-view-button"
                            onClick={() => handleViewTicket(report)}
                            size="small"
                            title="View & Reply"
                          >
                            <VisibilityIcon fontSize="small" />
                          </Button>
                          <Button
                            className="report-edit-button"
                            onClick={() => handleEditReport(report)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </Button>
                          <Button
                            className="report-delete-button"
                            onClick={() => handleDeleteReport(report._id)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          
          {/* Pagination - shown for both views */}
          <div className="tickets-pagination-container">
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              size="medium"
              siblingCount={1}
              boundaryCount={1}
              shape="rounded"
              className="tickets-pagination"
            />
          </div>
        </div>
      </div>

      {showModal && (
        <TicketModal
          open={showModal}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          report={editingReport}
          uid={uid}
        />
      )}

      {showDetailModal && selectedTicket && (
        <TicketDetailModal
          open={showDetailModal}
          onClose={handleDetailModalClose}
          ticket={selectedTicket}
          uid={uid}
          onUpdate={getReports}
        />
      )}
    </div>
  );
}

export default SupportTicket;
